import { z } from 'zod';

/** One pending/incomplete expense from `GET /api/v1/pending-expense`. Only consumed fields typed. */
const PendingExpenseDtoSchema = z.object({
  id: z.string(),
  pending_status: z.string().nullish(),
  assign_status: z.string().nullish(),
  last_pending_status: z.string().nullish(),
  is_private: z.boolean().default(false),
  // For prefilling the edit form: the group id, note, and recurrence rule (when present).
  context_id: z.string().nullish(),
  notes: z.string().nullish(),
  // Recurrence — the key/shape varies across the API, so accept the common variants and read them
  // DEFENSIVELY in the mapper (never fail the whole list parse). `is_recurring` is a fallback flag.
  recurring_rules: z.unknown().nullish(),
  recurring: z.unknown().nullish(),
  recurrence: z.unknown().nullish(),
  is_recurring: z.boolean().nullish(),
  // The API sends `null` for not-yet-entered amounts (not just omitting it), so `.default(0)`
  // alone wouldn't catch it — `.nullish()` accepts null/undefined; the mapper coalesces to 0.
  amount: z.number().nullish(),
  description: z.string().nullish(),
  date: z.string().nullish(),
  created_at: z.string().nullish(),
  expense_type_name: z.string().nullish(),
  context_name: z.string().nullish(),
  context_type: z.string().nullish(),
  attachment_url: z.string().nullish(),
  currency_symbol: z.string().nullish(),
  currency_code: z.string().nullish(),
  // Currency conversion — when the bill was entered in a currency other than the home currency, the
  // top-level `amount`/`currency_*` are the CONVERTED (home) values; the ORIGINAL bill amount +
  // currency are carried here (same shape as the group-ledger expense) and used to prefill edits.
  is_currency_converted: z.boolean().default(false),
  expense_amount: z.number().nullish(),
  expense_currency: z
    .object({ currency: z.string().nullish(), symbol: z.string().nullish() })
    .nullish(),
  // Paid-by legs, when the payload includes them — only the IDENTITIES are read (payer id +
  // wallet-vs-user); the edit form derives the amount from the totals, so the endpoint's amount unit
  // doesn't matter here. Absent → the edit form falls back to its default (current-user pays). The
  // GET may echo the create's `paid_by[]` (id key `id`) or the ledger's `payments[]` (key `user_id`),
  // so BOTH shapes are accepted and read defensively in the mapper.
  payments: z
    .array(
      z.object({
        type: z.string().nullish(),
        user_id: z.string().nullish(),
        id: z.string().nullish(),
      }),
    )
    .nullish(),
  paid_by: z
    .array(
      z.object({
        type: z.string().nullish(),
        user_id: z.string().nullish(),
        id: z.string().nullish(),
      }),
    )
    .nullish(),
  // Split configuration, when the payload includes it — used to prefill the edit form's Split tab +
  // per-member values. Amounts are MAJOR units here (like the top-level `amount`).
  split_configs: z
    .array(
      z.object({
        user_id: z.string().nullish(),
        split_type: z.string().nullish(),
        split_enabled: z.boolean().nullish(),
        amount: z.number().nullish(),
        expense_amount: z.number().nullish(),
        percentage: z.number().nullish(),
        shares: z.number().nullish(),
      }),
    )
    .nullish(),
  // Breakdown / itemization, when the payload includes it — used to prefill the edit form's breakdown
  // modal (line items + additional charges). `amount` is INTEGER MINOR units here (23400 = ₹234.00);
  // `sing` (sic — the backend key) is the sign, "-" for a discount charge. `type` is "item" | "charge".
  is_breakdown_added: z.boolean().nullish(),
  items: z
    .array(
      z.object({
        name: z.string().nullish(),
        amount: z.number().nullish(),
        type: z.string().nullish(),
        sing: z.string().nullish(),
      }),
    )
    .nullish(),
  // Location — sent as strings on the wire (create/update payload), so accept string OR number on
  // read too (defensive; the mapper coerces). Absent/empty ⇒ no location was attached.
  location: z.string().nullish(),
  location_lat: z.union([z.string(), z.number()]).nullish(),
  location_long: z.union([z.string(), z.number()]).nullish(),
  location_address1: z.string().nullish(),
  location_address2: z.string().nullish(),
  location_city: z.string().nullish(),
  location_state: z.string().nullish(),
  location_zip: z.string().nullish(),
  location_country: z.string().nullish(),
});

/** Envelope: `{ status, message, data: { expenses } }`. `data` is nullish-tolerant (→ empty). */
export const PendingExpenseListResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().optional(),
  data: z.object({ expenses: z.array(PendingExpenseDtoSchema).default([]) }).nullish(),
});

export type PendingExpenseDto = z.infer<typeof PendingExpenseDtoSchema>;
